/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@shared/components/ui/dialog';
import { Button } from '@shared/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/tabs';
import { Label } from '@shared/components/ui/label';
import { Input } from '@shared/components/ui/input';
import { Slider } from '@shared/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Badge } from '@shared/components/ui/badge';
import { Separator } from '@shared/components/ui/separator';
import { ScrollArea } from '@shared/components/ui/scroll-area';
import { useToast } from '@shared/components/ui/use-toast';
import { 
  Settings, 
  Copy, 
  Download, 
  RotateCcw, 
  Trash2, 
  Brain,
  Hash,
  Clock,
  Zap,
  Database,
  MessageSquare
} from 'lucide-react';
import { ChatMessage } from '@shared/contexts/ChatContext';
import { cn } from '@shared/lib/utils';

interface ChatSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messages: ChatMessage[];
  currentAgent: string;
  sessionId?: string;
  onResetChat: () => void;
  className?: string;
}

export function ChatSettingsModal({ 
  open, 
  onOpenChange, 
  messages, 
  currentAgent,
  sessionId,
  onResetChat,
  className 
}: ChatSettingsModalProps) {
  const { toast } = useToast();
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [model, setModel] = useState('claude-4-5-opus');

  // Calculate chat statistics
  const totalMessages = messages.length;
  const userMessages = messages.filter(m => m.role === 'user').length;
  const assistantMessages = messages.filter(m => m.role === 'assistant').length;
  
  const totalTokens = messages.reduce((acc, msg) => {
    if (msg.metadata?.tokenCount) {
      return acc + (msg.metadata.tokenCount.total || 0);
    }
    return acc;
  }, 0);

  const totalInputTokens = messages.reduce((acc, msg) => {
    if (msg.metadata?.tokenCount) {
      return acc + (msg.metadata.tokenCount.input || 0);
    }
    return acc;
  }, 0);

  const totalOutputTokens = messages.reduce((acc, msg) => {
    if (msg.metadata?.tokenCount) {
      return acc + (msg.metadata.tokenCount.output || 0);
    }
    return acc;
  }, 0);

  const totalProcessingTime = messages.reduce((acc, msg) => {
    if (msg.metadata?.processingTime) {
      return acc + msg.metadata.processingTime;
    }
    return acc;
  }, 0);

  const totalToolCalls = messages.reduce((acc, msg) => {
    return acc + (msg.toolCalls?.length || 0);
  }, 0);

  const totalEntityLinks = messages.reduce((acc, msg) => {
    return acc + (msg.entityLinks?.length || 0);
  }, 0);

  const handleDuplicateChat = () => {
    // TODO: Implement chat duplication
    toast({
      title: "Feature Coming Soon",
      description: "Chat duplication will be available in a future update.",
    });
  };

  const handleExportChat = () => {
    const chatData = {
      sessionId,
      agent: currentAgent,
      timestamp: new Date().toISOString(),
      messages: messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp.toISOString(),
        metadata: msg.metadata,
        toolCalls: msg.toolCalls,
        entityLinks: msg.entityLinks
      })),
      statistics: {
        totalMessages,
        userMessages,
        assistantMessages,
        totalTokens,
        totalInputTokens,
        totalOutputTokens,
        totalProcessingTime,
        totalToolCalls,
        totalEntityLinks
      }
    };

    const blob = new Blob([JSON.stringify(chatData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-export-${sessionId || 'session'}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Chat Exported",
      description: "Chat data has been downloaded as JSON file.",
    });
  };

  const handleResetChat = () => {
    onResetChat();
    onOpenChange(false);
    toast({
      title: "Chat Reset",
      description: "Chat history has been cleared.",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("max-w-2xl max-h-[80vh]", className)}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Chat Settings & Statistics
          </DialogTitle>
          <DialogDescription>
            View chat statistics, adjust model parameters, and manage your chat session.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="statistics" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="statistics">Statistics</TabsTrigger>
            <TabsTrigger value="model">Model Settings</TabsTrigger>
            <TabsTrigger value="actions">Actions</TabsTrigger>
          </TabsList>

          <TabsContent value="statistics" className="space-y-4">
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-6">
                {/* Session Info */}
                <div>
                  <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Session Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Current Agent</Label>
                      <Badge variant="secondary">{currentAgent}</Badge>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Session ID</Label>
                      <div className="text-xs font-mono bg-muted rounded px-2 py-1">
                        {sessionId || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Message Statistics */}
                <div>
                  <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Brain className="h-4 w-4" />
                    Message Statistics
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-muted/30 rounded">
                      <div className="text-lg font-semibold">{totalMessages}</div>
                      <div className="text-xs text-muted-foreground">Total Messages</div>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded">
                      <div className="text-lg font-semibold">{userMessages}</div>
                      <div className="text-xs text-muted-foreground">User Messages</div>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded">
                      <div className="text-lg font-semibold">{assistantMessages}</div>
                      <div className="text-xs text-muted-foreground">AI Messages</div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Token Usage */}
                <div>
                  <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    Token Usage
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-muted/30 rounded">
                      <div className="text-lg font-semibold">{totalTokens.toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">Total Tokens</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded border border-green-200">
                      <div className="text-lg font-semibold text-green-700">{totalInputTokens.toLocaleString()}</div>
                      <div className="text-xs text-green-600">Input Tokens</div>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded border border-blue-200">
                      <div className="text-lg font-semibold text-blue-700">{totalOutputTokens.toLocaleString()}</div>
                      <div className="text-xs text-blue-600">Output Tokens</div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Performance & Usage */}
                <div>
                  <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Performance & Usage
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-muted/30 rounded">
                      <div className="text-lg font-semibold">{(totalProcessingTime / 1000).toFixed(1)}s</div>
                      <div className="text-xs text-muted-foreground">Total Processing</div>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded">
                      <div className="text-lg font-semibold">{totalToolCalls}</div>
                      <div className="text-xs text-muted-foreground">Tool Calls</div>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded">
                      <div className="text-lg font-semibold">{totalEntityLinks}</div>
                      <div className="text-xs text-muted-foreground">Entity Links</div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="model" className="space-y-4">
            <div className="space-y-6">
              <div>
                <Label htmlFor="model-select">Model</Label>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="claude-4-5-opus">Claude 4 Sonnet</SelectItem>
                    <SelectItem value="claude-4-5-haiku">Claude 4.5 Haiku</SelectItem>
                    <SelectItem value="claude-4-5-sonnet">Claude 4.5 Sonnet</SelectItem>
                    <SelectItem value="claude-4-sonnet">Claude 4 Sonnet</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="temperature">Temperature: {temperature}</Label>
                <Slider
                  id="temperature"
                  min={0}
                  max={2}
                  step={0.1}
                  value={[temperature]}
                  onValueChange={(value) => setTemperature(value[0])}
                  className="mt-2"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  Higher values make responses more creative, lower values more focused.
                </div>
              </div>

              <div>
                <Label htmlFor="max-tokens">Max Tokens</Label>
                <Input
                  id="max-tokens"
                  type="number"
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(parseInt(e.target.value) || 2048)}
                  min={1}
                  max={8192}
                />
                <div className="text-xs text-muted-foreground mt-1">
                  Maximum number of tokens to generate in responses.
                </div>
              </div>

              <div className="text-sm text-muted-foreground bg-muted/30 rounded p-3">
                <strong>Note:</strong> These settings will apply to new messages. 
                Model settings may be overridden by agent-specific configurations.
              </div>
            </div>
          </TabsContent>

          <TabsContent value="actions" className="space-y-4">
            <div className="space-y-4">
              <div className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handleDuplicateChat}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate Chat Session
                </Button>

                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handleExportChat}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Chat Data
                </Button>

                <Separator />

                <Button 
                  variant="outline" 
                  className="w-full justify-start text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                  onClick={handleResetChat}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset Chat Session
                </Button>

                <div className="text-sm text-muted-foreground bg-yellow-50 border border-yellow-200 rounded p-3">
                  <strong>Warning:</strong> Resetting the chat will permanently delete all messages 
                  and cannot be undone. Consider exporting your chat first.
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
